import { useCallback, useEffect, useMemo, useState } from 'react'

import CollectionsIcon from '@mui/icons-material/Collections'
import EditIcon from '@mui/icons-material/Edit'
import EmailIcon from '@mui/icons-material/Email'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import Link from '@mui/icons-material/Link'
import SearchIcon from '@mui/icons-material/Search'
import WhatsApp from '@mui/icons-material/WhatsApp'
import { TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import type {
  GridColDef,
  GridValueGetterParams,
  GridRenderCellParams,
  GridFilterItem,
  GridSortModel,
} from '@mui/x-data-grid'
import ConfirmationModal from 'components/ConfirmationModal'
import DataTable from 'components/DataTable'
import LoadingFullPage from 'components/LoadingFullPage'
import Select from 'components/Select'
import { ROUTES } from 'constants/routes'
import { useGlobalState } from 'contexts/global-state'
import { useQueryParams } from 'hooks/useQueryParams'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { ApiService } from 'services/api'
import formatCurrencyUnit from 'utils/formatCurrencyUnit'
import { removeEmptyFieldsInObjectFieldsInObject } from 'utils/removeEmptyFieldsInObject'
import {
  WHATSAPP_FREE_MESSAGE_MAX_LENGTH,
  getWhatsappMessageLength,
  limitWhatsappMessageInput,
} from 'utils/whatsapp-message'

import { FinishedButton } from './components/FinishedButton'

export const RESERVATION_SITUATION_TYPES = {
  all: 'Todas',
  waiting_payment: 'Aguardando confirmação',
  payment_confirmed: 'Reserva confirmada',
  cancelled: 'Cancelado',
  payment_reversal: 'Devolvida',
  finished: 'Concluída',
}

export const RESERVATION_SITUATIONS_OPTIONS = Object.entries(
  RESERVATION_SITUATION_TYPES,
).map(([key, value]) => ({ label: value, value: key }))

interface FormFilters {
  code?: string
}

const ACTIVITY_STATUS_MAP = {
  opened: 'Em aberto',
  confirmed: 'Confirmada',
  cancelled: 'Cancelada',
  finished: 'Concluída',
}

export default function Activities() {
  const navigate = useNavigate()
  const query = useQueryParams()

  const reactHookFormSearch = useForm<FormFilters>()
  const status_checklist = query.get('status_checklist')
  const [searchParams, setSearchParams] = useState<GenericObject[]>(() => {
    const status = query.getAll('status')
    const start_travel_date_period = query.get('start_travel_date_period')

    return [
      {
        status,
        status_checklist,
        start_travel_date_period,
      },
    ]
  })

  const [filterOptions, setFilterOptions] = useState<GridFilterItem[]>([])
  const [sortOptions, setSortOptions] = useState<GridSortModel>([])

  const { openSuccessToast, openErrorToast } = useGlobalState()

  const [messageModal, setMessageModal] = useState(false)
  const [mailConfirmationTeachersModal, setMailConfirmationTeachersModal] =
    useState(false)
  const [mailAlbumLinkTeachersModal, setMailAlbumLinkModal] = useState(false)
  const [currentRow, setCurrentRow] = useState()
  const [situation, setSituation] = useState<ReservationSituation | 'all'>(
    'all',
  )
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)

  const onConfirmMessage = useCallback(async () => {
    setIsSending(true)
    await ApiService.Activities.sendMessage({
      id: Number(currentRow),
      message,
      situation,
    })
      .then(() => {
        openSuccessToast({ message: 'A mensagem foi enviada' })
        setIsSending(false)
        setMessageModal(false)
      })
      .catch((error) => {
        const message = error?.message || 'Erro ao enviar mensagem.'
        openErrorToast({ message })
        setIsSending(false)
        setMessageModal(false)
      })
  }, [currentRow, message, situation, openErrorToast, openSuccessToast])

  const onConfirmMailConfirmationTeachers = useCallback(async () => {
    setIsSending(true)
    await ApiService.Activities.sendMailConfirmationTeachers({
      id: Number(currentRow),
    })
      .then(() => {
        openSuccessToast({ message: 'A mensagem foi enviada' })
        setIsSending(false)
        setMailConfirmationTeachersModal(false)
      })
      .catch((error) => {
        const message = error?.message || 'Erro ao enviar mensagem.'
        openErrorToast({ message })
        setIsSending(false)
        setMailConfirmationTeachersModal(false)
      })
  }, [currentRow, openErrorToast, openSuccessToast])

  const onConfirmMailAlbumLink = useCallback(async () => {
    setIsSending(true)
    await ApiService.Activities.sendMailAlbumLink({
      id: Number(currentRow),
    })
      .then(() => {
        openSuccessToast({ message: 'A mensagem foi enviada' })
        setIsSending(false)
        setMailAlbumLinkModal(false)
      })
      .catch((error) => {
        const message = error?.message || 'Erro ao enviar mensagem.'
        openErrorToast({ message })
        setIsSending(false)
        setMailAlbumLinkModal(false)
      })
  }, [currentRow, openErrorToast, openSuccessToast])

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'code', headerName: 'Código', flex: 1 },
      {
        field: 'institution.fantasy_name',
        headerName: 'Instituição',
        flex: 1,
        relation: {
          name: 'activity.institution',
          alias: 'institution',
        },
        valueGetter: (
          params: GridValueGetterParams<Reservation['institution']>,
        ) => params.row?.institution?.fantasy_name,
      },
      {
        field: 'commercialUnit.fantasy_name',
        headerName: 'Unidade Comercial',
        flex: 1,
        relation: {
          name: 'activity.commercialUnit',
          alias: 'commercialUnit',
        },
        valueGetter: (
          params: GridValueGetterParams<Reservation['commercialUnit']>,
        ) => params.row?.commercialUnit?.fantasy_name,
      },
      { field: 'name', headerName: 'Nome', flex: 1 },
      {
        field: 'price',
        headerName: 'Preço',
        flex: 1,
        valueGetter: (params: GridValueGetterParams<Activity>) =>
          formatCurrencyUnit(+params.row.price),
      },
      {
        field: 'start_travel_date',
        headerName: 'Data início',
        flex: 1,
        valueGetter: (params: GridValueGetterParams<Activity>) =>
          params.row.start_travel_date.substring(0, 10),
      },
      {
        field: 'end_travel_date',
        headerName: 'Data fim',
        flex: 1,
        valueGetter: (params: GridValueGetterParams<Activity>) =>
          params.row.end_travel_date.substring(0, 10),
      },
      {
        field: 'enrollment_limit',
        headerName: 'Data limite de inscrição',
        flex: 1,
        valueGetter: (params: GridValueGetterParams<Activity>) =>
          params.row.enrollment_limit.substring(0, 10),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        valueFormatter: (field) =>
          ACTIVITY_STATUS_MAP[field.value as ActivityStatus],
      },
      {
        field: 'actions',
        headerName: 'Ações',
        width: 240,
        sortable: false,
        exclude: true,
        renderCell: (params: GridRenderCellParams<Institution>) => {
          return (
            <div style={{ display: 'flex', gap: '12px' }}>
              <FinishedButton
                activity={params.row}
                type="icon"
                onLoading={(value) => setIsSending(value)}
              />
              {(params.row.status === 'confirmed' ||
                params.row.status === 'finished' ||
                params.row.status === 'opened') && (
                <FactCheckOutlinedIcon
                  fontSize="small"
                  onClick={() => {
                    navigate(ROUTES.ACTIVITY_CHECKLIST.EDIT(params.row.id))
                  }}
                />
              )}
              <WhatsApp
                fontSize="small"
                onClick={() => {
                  setCurrentRow(params.row.id)
                  setMessage('')
                  setMessageModal(true)
                }}
              />
              <EmailIcon
                titleAccess="Enviar confirmação para professores"
                fontSize="small"
                onClick={() => {
                  setCurrentRow(params.row.id)
                  setMailConfirmationTeachersModal(true)
                }}
              />
              {params.row.status === 'finished' && (
                <CollectionsIcon
                  titleAccess="Enviar link do álbum para responsáveis"
                  fontSize="small"
                  onClick={() => {
                    setCurrentRow(params.row.id)
                    setMailAlbumLinkModal(true)
                  }}
                />
              )}
              <Link
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const url = `${process.env.REACT_APP_SITE_URL}/a/${params.row.code}`
                  navigator.clipboard
                    .writeText(url)
                    .then(() => {
                      openSuccessToast({ message: 'Link copiado' })
                    })
                    .catch(() => {
                      openErrorToast({ message: 'Erro ao copiar link' })
                    })
                }}
              />

              <SearchIcon
                onClick={() => {
                  navigate(ROUTES.ACTIVITIES.VIEW(params.row.id))
                }}
              />

              <EditIcon
                onClick={() => {
                  navigate(ROUTES.ACTIVITIES.EDIT(params.row.id))
                }}
              />
            </div>
          )
        },
      },
    ],
    [navigate, openErrorToast, openSuccessToast],
  )

  const handleFilter = useCallback(() => {
    const data = removeEmptyFieldsInObjectFieldsInObject(
      reactHookFormSearch.getValues() as GenericObject,
    )
    setSearchParams([
      {
        ...data,
      },
    ])
  }, [reactHookFormSearch])

  useEffect(() => {
    if (status_checklist === 'pending') {
      setSortOptions([
        {
          field: 'start_travel_date',
          sort: 'asc',
        },
      ])
    }
  }, [status_checklist])

  if (isSending) {
    return <LoadingFullPage />
  }

  return (
    <>
      <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
        <Container maxWidth={false}>
          <Box>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ m: 1 }} variant="h4">
                Atividades
              </Typography>

              <Box sx={{ m: 1 }}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    navigate(ROUTES.ACTIVITIES.CREATE)
                  }}
                >
                  Adicionar
                </Button>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
              marginTop: '20px',
            }}
          >
            <TextField
              label="Código"
              type="text"
              variant="outlined"
              {...reactHookFormSearch.register('code')}
            />

            <Button color="primary" variant="contained" onClick={handleFilter}>
              Pesquisar
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => setSearchParams([])}
            >
              Todos
            </Button>
            <Button
              color="success"
              variant="contained"
              onClick={() => navigate(ROUTES.ACTIVITIES.LIST_CALENDAR)}
            >
              Calendário
            </Button>
          </Box>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <DataTable
                columns={columns}
                entity="Activity"
                source="activity"
                onFilterModelChange={(newValue) => {
                  setFilterOptions(newValue.items)
                }}
                onSortModelChange={(newValue) => {
                  setSortOptions(newValue)
                }}
                searchParameters={searchParams}
                likeParameters={filterOptions}
                sortParameters={sortOptions}
              />
            </CardContent>
          </Card>
        </Container>
      </Box>
      <ConfirmationModal
        title="Enviar Mensagem"
        opened={messageModal}
        onClose={() => setMessageModal(false)}
        onConfim={onConfirmMessage}
        onCancel={() => setMessageModal(false)}
      >
        <label htmlFor="message-wpp"></label>
        <textarea
          name="message-wpp"
          style={{ width: '100%' }}
          rows={5}
          value={message}
          onChange={(e) =>
            setMessage(limitWhatsappMessageInput(e.target.value))
          }
        ></textarea>
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
        >
          {getWhatsappMessageLength(message)}/{WHATSAPP_FREE_MESSAGE_MAX_LENGTH}
        </Typography>
        <Select
          id="situation"
          label={'Status da reserva'}
          onChange={(e) =>
            setSituation(String(e.target.value) as ReservationSituation)
          }
          value={situation}
          options={RESERVATION_SITUATIONS_OPTIONS}
          autoFill
        />
      </ConfirmationModal>
      <ConfirmationModal
        title="Enviar Email de confirmação para professores"
        opened={mailConfirmationTeachersModal}
        onClose={() => setMailConfirmationTeachersModal(false)}
        onConfim={onConfirmMailConfirmationTeachers}
        onCancel={() => setMailConfirmationTeachersModal(false)}
      >
        <p>
          Deseja enviar email e Whatsapp de confirmação de atividade para <br />{' '}
          todos os professores dessa atividadde?
        </p>
      </ConfirmationModal>
      <ConfirmationModal
        title="Enviar Email com link do álbum para responsáveis"
        opened={mailAlbumLinkTeachersModal}
        onClose={() => setMailAlbumLinkModal(false)}
        onConfim={onConfirmMailAlbumLink}
        onCancel={() => setMailAlbumLinkModal(false)}
      >
        <p>
          Deseja enviar email com link do álbum para <br /> todos os
          responsáveis?
        </p>
      </ConfirmationModal>
    </>
  )
}
